/**
 * Tours Query Hooks — BE completion state behind the feature tour engine.
 *
 * One list read per session (the BE limiter is 30 req/min shared with the
 * completion POST); a completion patches that cache from the POST response.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TourStatus } from '@/types';
import { toursClient } from '../client/tours.client';
import { queryKeys } from '../query-keys';

/** Published tours + web/mobile completion for the current user. */
export function useTourStatuses(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.tours.list(),
    queryFn: toursClient.list,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Auto-start fails closed, so a retry buys nothing and spends the limiter.
    retry: false,
  });
}

/**
 * Marks a tour complete on web. No-op (no request) for a slug the list does
 * not carry — unpublished or unknown rows 404 — or one already completed.
 */
export function useCompleteTour() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const statuses = queryClient.getQueryData<TourStatus[]>(queryKeys.tours.list());
      const row = statuses?.find((t) => t.slug === slug);
      if (!row || row.completed_web_at) return null;
      return toursClient.complete(slug);
    },
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData<TourStatus[]>(queryKeys.tours.list(), (prev) =>
        prev?.map((t) => (t.slug === updated.slug ? updated : t))
      );
    },
  });
}
